import { FC, PropsWithChildren, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { Flex, FlexProps } from 'antd';
import { SampleDataSubjectValidFormDataType } from '@atlas-frontend/sdk';
import { useSampleContext } from '../state/SampleContext';
import { SampleSubjectLockIcon } from './SampleSubjectLockIcon';

export interface WithSampleFilterOptionLabelProps extends FlexProps {
  groupMemberId?: number;
}

export const WithSampleSubjectsLockIndicator: FC<
  WithSampleFilterOptionLabelProps
> = (props) => {
  const {
    groupMemberId,
    children,
    justify = 'space-between',
    align = 'center',
    gap = 'middle',
    ...rest
  } = props;
  const { sampleType } = useSampleContext();

  return (
    <Flex justify={justify} align={align} gap={gap} {...rest}>
      {sampleType === 'group' ? (
        <FilterOptionLabelInner groupMemberId={groupMemberId}>
          {children}
        </FilterOptionLabelInner>
      ) : (
        children
      )}
    </Flex>
  );
};

const FilterOptionLabelInner: FC<
  PropsWithChildren<{
    groupMemberId?: number;
  }>
> = (props) => {
  const samples = useWatch({ name: 'samples' });

  return props.groupMemberId ? (
    <GroupMemberFilterOptionLabel {...props} samples={samples} />
  ) : (
    <GroupWideFilterOptionLabel {...props} samples={samples} />
  );
};

const GroupMemberFilterOptionLabel: FC<
  PropsWithChildren<{
    groupMemberId?: number;
    samples: SampleDataSubjectValidFormDataType[];
  }>
> = (props) => {
  const { samples, groupMemberId, children } = props;

  const isLocked = useMemo(() => {
    if (!samples.length) {
      return false;
    }
    const count = samples.reduce(
      (count, sample) =>
        sample.isLocked ||
        sample.sampleDataObjects.some(
          (o) => o.dataObjectDescriptionId === groupMemberId && o.isLocked,
        )
          ? count + 1
          : count,
      0,
    );
    return count === samples.length || (count > 0 && 'some');
  }, [samples, groupMemberId]);

  return (
    <>
      {children}
      {isLocked && <SampleSubjectLockIcon isLocked={isLocked} />}
    </>
  );
};

const GroupWideFilterOptionLabel: FC<
  PropsWithChildren<{
    samples: SampleDataSubjectValidFormDataType[];
  }>
> = (props) => {
  const { samples, children } = props;

  const isLocked = useMemo(
    () =>
      Boolean(samples.length) &&
      (samples.every((sample) => sample.isLocked) ||
        ((samples as { isLocked: boolean }[])
          .concat(samples.map((sample) => sample.sampleDataObjects).flat())
          .some((sample) => sample.isLocked) &&
          'some')),
    [samples],
  );

  return (
    <>
      {children}
      {isLocked && <SampleSubjectLockIcon isLocked={isLocked} />}
    </>
  );
};
